import React, { useEffect, useState } from "react";
import { cn } from "../../lib/utils";
import { getContentImageUrls } from "../../lib/contentImages";

/**
 * Displays post/event images. A single image is static; multiple images
 * use previous/next arrows and clickable pagination dots.
 *
 * @param {Object} props
 * @param {object} props.post
 * @returns {JSX.Element | null}
 */
function MediaFrame({ post }) {
  const urls = getContentImageUrls(post);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [post?.id]);

  useEffect(() => {
    if (index >= urls.length) {
      setIndex(0);
    }
  }, [index, urls.length]);

  if (!urls.length) {
    return null;
  }

  const current = Math.min(index, urls.length - 1);
  const isCarousel = urls.length > 1;

  return (
    <div className="media-frame mt-3.5 w-full min-w-0">
      <div className="relative aspect-[3/2] w-full overflow-hidden rounded-card border border-ui-border">
        <div
          className="flex h-full transition-transform duration-300 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {urls.map((url, imageIndex) => (
            <img
              key={`${url}-${imageIndex}`}
              src={url}
              alt={isCarousel ? `Image ${imageIndex + 1} of ${urls.length}` : "Post"}
              className="h-full w-full shrink-0 basis-full object-cover"
            />
          ))}
        </div>
        {isCarousel && current > 0 ? (
          <button
            type="button"
            aria-label="Previous image"
            onClick={(event) => {
              event.stopPropagation();
              setIndex((value) => Math.max(0, value - 1));
            }}
            className="absolute left-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-carousel-control text-carousel-control-fg shadow-shell-sm"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        ) : null}
        {isCarousel && current < urls.length - 1 ? (
          <button
            type="button"
            aria-label="Next image"
            onClick={(event) => {
              event.stopPropagation();
              setIndex((value) => Math.min(urls.length - 1, value + 1));
            }}
            className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-carousel-control text-carousel-control-fg shadow-shell-sm"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : null}
      </div>
      {isCarousel ? (
        <div className="mt-2 flex items-center justify-center gap-1.5" role="tablist" aria-label="Images">
          {urls.map((_, dotIndex) => (
            <button
              key={dotIndex}
              type="button"
              role="tab"
              aria-selected={dotIndex === current}
              aria-label={`Image ${dotIndex + 1} of ${urls.length}`}
              onClick={(event) => {
                event.stopPropagation();
                setIndex(dotIndex);
              }}
              className="flex h-4 w-4 items-center justify-center"
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full transition-colors duration-300 motion-reduce:transition-none",
                  dotIndex === current ? "bg-carousel-dot-active" : "bg-carousel-dot"
                )}
              />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default MediaFrame;
